import { Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import SeoHead from "@/components/feature/SeoHead";
import { movies, categories, articles, faqs } from "@/mocks/movies";

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function Home() {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredMovies =
    activeCategory === "all"
      ? movies
      : movies.filter((m) => m.category === activeCategory);

  const featuredMovies = movies.filter((m) => m.featured);
  const latestMovies = [...movies].sort((a, b) => b.year - a.year).slice(0, 8);

  const displayCategories = categories.filter(
    (c) => c.slug !== "all" && c.slug !== "documentary"
  );

  const seoTitle = "春夜影院 - 免费高清电影电视剧在线观看，蓝光画质流畅播放";
  const seoDescription =
    "春夜影院在线观看官网入口与最新地址，提供手机版免费高清电影电视剧大全，蓝光画质不缓冲流畅播放，最新电影综艺免费在线观看，便捷访问春夜影院网。";
  const seoKeywords =
    "春夜影院,在线观看,官网入口,最新地址,手机版,免费高清电影,电视剧大全,蓝光画质,不缓冲,流畅播放,最新电影,综艺免费,春夜影院网";

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "春夜影院",
    url: "https://vxrboyl.cn",
    description: seoDescription,
    potentialAction: {
      "@type": "SearchAction",
      target: "https://vxrboyl.cn/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "春夜影院",
    url: "https://vxrboyl.cn",
    description: "春夜影院提供免费高清电影电视剧在线观看",
    sameAs: ["https://vxrboyl.cn"],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://vxrboyl.cn"
        schema={{ ...websiteSchema, ...organizationSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinema%20theater%20interior%20with%20warm%20ambient%20lighting%2C%20red%20velvet%20curtains%2C%20empty%20seats%2C%20large%20movie%20screen%20with%20soft%20glow%2C%20cinematic%20atmosphere%2C%20elegant%20gold%20accents%2C%20dramatic%20mood%2C%20luxury%20theater%20design%2C%20high%20contrast%20lighting%2C%20rich%20dark%20tones&width=1600&height=900&seq=hero-cinema-bg&orientation=landscape"
            alt="春夜影院 - 蓝光高清在线观影"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>

          <div className="relative z-10 w-full max-w-[900px] mx-auto text-center px-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-50 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              春夜影院
            </h1>
            <p className="text-lg md:text-xl text-foreground-200/90 mb-3">
              免费高清电影电视剧在线观看
            </p>
            <p className="text-sm md:text-base text-foreground-300/80 mb-8 max-w-[600px] mx-auto">
              蓝光画质 · 流畅播放不缓冲 · 手机版便捷访问 · 每日更新最新资源
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-foreground-50 font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm hover:scale-105"
              >
                <i className="ri-play-circle-line mr-2"></i>立即观影
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/10 hover:bg-white/20 text-foreground-100 font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm border border-white/20 hover:scale-105"
              >
                <i className="ri-information-line mr-2"></i>了解更多
              </Link>
            </div>

            <div className="mt-10 flex items-center justify-center gap-6 text-foreground-400 text-xs">
              <span className="flex items-center gap-1"><i className="ri-hd-line text-base"></i> 蓝光画质</span>
              <span className="flex items-center gap-1"><i className="ri-smartphone-line text-base"></i> 手机版</span>
              <span className="flex items-center gap-1"><i className="ri-speed-up-line text-base"></i> 不缓冲</span>
              <span className="flex items-center gap-1"><i className="ri-vip-crown-line text-base"></i> 免费观看</span>
            </div>
          </div>
        </section>

        {/* Category Quick Nav */}
        <section className="py-8 bg-background-950 border-b border-background-800">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-foreground-50"
                      : "bg-background-800 text-foreground-400 hover:text-foreground-200 hover:bg-background-700"
                  }`}
                  onClick={() => setActiveCategory(cat.slug)}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Movies */}
        <section className="py-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  <i className="ri-fire-line text-accent-500 mr-2"></i>热门推荐
                </h2>
                <p className="text-foreground-500 text-sm mt-1">精选高分好片，不容错过</p>
              </div>
              <Link
                to="/category/all"
                className="text-primary-500 hover:text-primary-400 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {featuredMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  title={movie.title}
                  slug={movie.slug}
                  year={movie.year}
                  rating={movie.rating}
                  categoryName={getCategoryName(movie.category)}
                  posterUrl={movie.posterUrl}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Category Sections */}
        {displayCategories.slice(0, 3).map((cat) => {
          const catMovies = movies
            .filter((m) => m.category === cat.slug)
            .slice(0, 6);
          if (catMovies.length === 0) return null;

          return (
            <section key={cat.slug} className="py-10 bg-background-100">
              <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                      {cat.name}
                    </h3>
                    <p className="text-foreground-500 text-sm mt-1">精选{cat.name}佳作</p>
                  </div>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-primary-500 hover:text-primary-400 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    更多 <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {catMovies.map((movie) => (
                    <MovieCard
                      key={movie.id}
                      id={movie.id}
                      title={movie.title}
                      slug={movie.slug}
                      year={movie.year}
                      rating={movie.rating}
                      categoryName={getCategoryName(movie.category)}
                      posterUrl={movie.posterUrl}
                    />
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* Latest Updates */}
        <section className="py-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  <i className="ri-timer-flash-line text-accent-500 mr-2"></i>最新更新
                </h2>
                <p className="text-foreground-500 text-sm mt-1">实时更新，精彩不断</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {latestMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  title={movie.title}
                  slug={movie.slug}
                  year={movie.year}
                  rating={movie.rating}
                  categoryName={getCategoryName(movie.category)}
                  posterUrl={movie.posterUrl}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Movie News Articles */}
        <section className="py-12 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  <i className="ri-article-line text-accent-500 mr-2"></i>影视资讯
                </h2>
                <p className="text-foreground-500 text-sm mt-1">最新电影资讯与深度影评</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 cursor-pointer hover:border-primary-500/30 transition-all duration-300"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={article.coverUrl}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <span className="absolute top-3 left-3 bg-primary-500 text-foreground-50 text-xs px-2 py-1 rounded-md">
                      {article.category === "news" ? "资讯" : article.category === "feature" ? "专题" : "影评"}
                    </span>
                  </div>
                  <div className="p-4">
                    <h4 className="text-foreground-950 font-semibold text-sm group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <span className="text-foreground-600 text-xs">{article.publishedAt}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm mt-2">关于春夜影院的常见疑问解答</p>
            </div>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 text-sm list-none">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 group-open:rotate-180"></i>
                  </summary>
                  <div className="px-5 pb-5 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-background-950">
          <div className="max-w-[800px] mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              立即开启您的观影之旅
            </h2>
            <p className="text-foreground-400 mb-8 max-w-[500px] mx-auto text-sm">
              海量高清影片，每日更新，完全免费。春夜影院，随时随地畅享影院级观影体验。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-8 py-3 bg-primary-500 hover:bg-primary-600 text-foreground-50 font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm hover:scale-105"
            >
              <i className="ri-play-circle-line mr-2 text-lg"></i>开始观影
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}